import { ProfileKeyCode } from "../DynamicProfileEnums";
import { DynamicProfileStoreBase } from "../DynamicProfileStoreBase";
import { DynamicCrossSectionHub } from "./DynamicCrossSectionHub";
import { doEvents } from 'cd-eft/utils/sysUtils';
import stores from 'cd-eft/models/Stores';
import { DynamicProfileClass } from "../DynamicProfileClass";
import { observable } from "mobx";
import { Graphic, Point, GraphicsLayer} from 'cd-eft/utils/esriTypes';
import * as profileSymbolsAndLabels from 'cd-eft/models/Stores/DynamicProfile/profileSymbolsAndLabels';
import { AppConstants } from 'cd-eft/appConstants';



const { waterwayDesignStore, threeCrossSectionDesignStore } = stores.AppStore;
class DynamicCrossSectionProfileStore extends DynamicProfileStoreBase {
    protected hub: DynamicCrossSectionHub;
    waterwayObject;
    currentObject;
    private _initialThreeCrossSectionPoint;
    @observable isMainSecDisplay: boolean = false;
    private _designedStations: any[] = [];
    private _originalStations: any[] = [];
    private _waterlevelStations: any[] = [];
    private _hasHoverTooltipListener = false;
    protected _circularCueLayer: GraphicsLayer | undefined;

    constructor(hub: DynamicCrossSectionHub) {
        super();

        this.hub = hub;
        this.profileKeys = [ProfileKeyCode.CSD, ProfileKeyCode.CSO, ProfileKeyCode.WTL];
        this.waterwayObject = waterwayDesignStore.waterwayDesignObject;
        this.currentObject = threeCrossSectionDesignStore.threeCrossSectionObject;
    }

    async createChannelFromSavedUgo(): Promise<void> { }
    async updateUgoTableOnChangeStationValue(): Promise<void> { }
    protected handlePolylineCreateEvent(event): void {}
    protected handlePointCreateEvent(event): void {}
    protected handleReshapeEvent(event): void {}
    protected initSketchVm(): void {}
    protected removeSketch(): void {}
    protected setUpTerraceAlignmentView(event: any): void {}
    async redrawUgoProfiles(): Promise<void> { }

    // SetStore
    set setStore(setStore) {
        if (this.hub) { this.hub.setStore = setStore }
    }

    get setStore() {
        return this.hub.setStore;
    }

    private get initialThreeCrossSectionPoint() {
        if (!this._initialThreeCrossSectionPoint) {
            this._initialThreeCrossSectionPoint = threeCrossSectionDesignStore.initialThreeCrossSectionPoint;
        }
        return this._initialThreeCrossSectionPoint;
    }

    initProfileKeys() {
        this.profileKeys = [ProfileKeyCode.CSD, ProfileKeyCode.CSO,ProfileKeyCode.WTL];
        this.profileKeys.forEach((keyCode) => {
            this.reset(keyCode);
        });
    }


    updateGridValues(gridValue: number, updateLines: boolean = true) {
        this.updateUserGridValues(gridValue, updateLines);
    }

    async redrawProfiles(bufferWidth) {
        try {
            this.raiseStartLengthyProcessEvent(true);
            await doEvents();

            // reset only necessary
            this.maxXmin = 1000000;
            this.maxXmax = -1000;
            this.maxYmin = 1000000;
            this.maxYmax = -1000;
            
            this.threeCrossSectionBufferWidth = bufferWidth;

            const yValues = this.getTCSMaxMinYValues(this._designedStations, this._originalStations);
            const designedProfile = this.profiles[ProfileKeyCode[3]] as DynamicProfileClass;
            designedProfile.ymax = yValues.ymax;
            designedProfile.ymin = yValues.ymin;
            designedProfile.tcsBufferWidth = this.threeCrossSectionBufferWidth;
            this.threeCrossSectionStartDist = Math.round(this._designedStations[0].distance) - this.threeCrossSectionBufferWidth;
            this.threeCrossSectionEndDist = Math.round(this._designedStations[this._designedStations.length - 1].distance) + this.threeCrossSectionBufferWidth;
            const totalDist = Math.abs(this.threeCrossSectionStartDist) + this.threeCrossSectionEndDist;
            designedProfile.drawProfileGraphics(totalDist);       

            const originalProfile = this.profiles[ProfileKeyCode[2]] as DynamicProfileClass;
            originalProfile.groundProfileLayer.removeAll();
            originalProfile.ymax = yValues.ymax;
            originalProfile.ymin = yValues.ymin;
            originalProfile.tcsBufferWidth = this.threeCrossSectionBufferWidth;
            originalProfile.drawProfileGraphics(totalDist)

            const waterLevelProfile = this.profiles[ProfileKeyCode[9]] as DynamicProfileClass;
            waterLevelProfile.groundProfileLayer.removeAll();
            waterLevelProfile.ymax = yValues.ymax;
            waterLevelProfile.ymin = yValues.ymin;
            waterLevelProfile.tcsBufferWidth = this.threeCrossSectionBufferWidth;
            waterLevelProfile.drawProfileGraphics(totalDist);

            this.drawCircularCueLayer(this._designedStations);
        } catch {

        } finally {
            this.raiseStartLengthyProcessEvent(false);
        }
    }

    async generateReachProfile(profile: any, displayTable: boolean = false) {
        try {
            this.raiseStartLengthyProcessEvent(true);
            await doEvents();
      
            if (profile) {
                this._designedStations = [];
                this._originalStations = [];
                this._waterlevelStations = [];

                this.reset(2);
                this.reset(3);
                this.reset(9);
       
                // Draw Cross Section Designed
                if (displayTable) {
                    this.isMainSecDisplay = true;
                    this.currentObject.threeCrossSectionPoints = [];
                    const points = this.currentObject.threeCrossSectionPoints;
                    profile.designProfile?.stationDTOList?.map((station) => {
                        this._designedStations.push(station);
                        if (station.inputPoint?.description === "StructurePoint") {
                            const newPoint = { ...this.initialThreeCrossSectionPoint };
                            newPoint.distance = station.distance?.toFixed(1);
                            newPoint.originalElevation = station.inputPoint.z! - station.inputPoint.cutOrFill!;
                            newPoint.designElevation = station.inputPoint.z;
                            newPoint.cutFillHeight = station.inputPoint.cutOrFill;
                            points.push(newPoint);
                        }
                    });
                } else {
                    profile.designProfile?.stationDTOList?.map((station) => {
                        this._designedStations.push(station);
                    });
                }
                // Get ymax and ymin
                let yValues;
                if (this._designedStations && profile.originalProfile?.stationDTOList) {
                    yValues = this.getTCSMaxMinYValues(this._designedStations, profile.originalProfile?.stationDTOList);

                    const designedProfile = this.profiles[ProfileKeyCode[3]] as DynamicProfileClass;
                    designedProfile.ymax = yValues.ymax;
                    designedProfile.ymin = yValues.ymin;
                    designedProfile.mapGroundProfilePoints(this._designedStations);
                    designedProfile.tcsBufferWidth = this.threeCrossSectionBufferWidth;
                    this.threeCrossSectionStartDist = Math.round(this._designedStations[0].distance) - this.threeCrossSectionBufferWidth;
                    this.threeCrossSectionEndDist = Math.round(this._designedStations[this._designedStations.length - 1].distance) + this.threeCrossSectionBufferWidth;
                    const totalDist = Math.abs(this.threeCrossSectionStartDist) + this.threeCrossSectionEndDist;
                    designedProfile.drawProfileGraphics(totalDist);

                    // Draw Cross Section Original
                    const originalProfile = this.profiles[ProfileKeyCode[2]] as DynamicProfileClass;
                    originalProfile.ymax = yValues.ymax;
                    originalProfile.ymin = yValues.ymin;
                    this._originalStations = [...profile.originalProfile?.stationDTOList];
                    originalProfile.mapGroundProfilePoints(this._originalStations);
                    originalProfile.tcsBufferWidth = this.threeCrossSectionBufferWidth;
                    originalProfile.drawProfileGraphics(totalDist);

                     // WaterLevel
                    const waterLevelProfile = this.profiles[ProfileKeyCode[9]] as DynamicProfileClass;
                    waterLevelProfile.ymax = yValues.ymax;
                    waterLevelProfile.ymin = yValues.ymin;
                    // Process WaterLevel Stations
                    this._waterlevelStations = [...this.processWaterLevelStations(profile)];
                    waterLevelProfile.mapGroundProfilePoints(this._waterlevelStations);
                    waterLevelProfile.tcsBufferWidth = this.threeCrossSectionBufferWidth;
                    waterLevelProfile.drawProfileGraphics(totalDist);
                    this.drawCircularCueLayer(profile.designProfile?.stationDTOList);
                }
            }
        } catch {
        } finally {
            this.raiseStartLengthyProcessEvent(false);
        }
    }

    private processWaterLevelStations(profile: any): any {
        // Step 1: Collect points
        const relevantPoints = profile.designProfile?.stationDTOList?.filter((station: any) =>
            ["WaterLevel", "Breakline - RightCorner", "Breakline - LeftCorner", "Border"].includes(station.inputPoint?.pointName)
        ) || [];
    
        // Step 2: Split points into two groups based on distance
        const group1 = relevantPoints.filter((point: any) => point.distance < 0);
        const group2 = relevantPoints.filter((point: any) => point.distance >= 0);
    
        // pick the best point based on the hierarchy
        const pickBestPoint = (group: any[]): any | null => {
            let selectedPoint = group.find(point => point.inputPoint.pointName === "WaterLevel");
            if (!selectedPoint) {
                selectedPoint = group.find(point => point.inputPoint.pointName.includes("Corner"));
            }
            if (!selectedPoint) {
                selectedPoint = group.find(point => point.inputPoint.pointName === "Border");
            }
            return selectedPoint || null;
        };
    
        // Step 3: Pick the best point from each group
        let point1 = pickBestPoint(group1);
        let point2 = pickBestPoint(group2);

        // Handle the case where both groups only have "Border" points. Do not draw waterlevel.
        if (point1?.inputPoint.pointName === "Border" && point2?.inputPoint.pointName === "Border") {
            point1=point2=null;
        }
    
        if(point1 && point2){
            return [point1, point2]
        }
    }
    
    private processOffsetPoints(stationDTOList?: any): any[] {
        return stationDTOList?.filter((station: any) => {
            const name = station.inputPoint?.pointName || "";
            return name.includes("Offset");
        }) || [];
    }
    private processOTCPoints(stationDTOList: any): any[] {
        return stationDTOList?.filter((station: any) => {
            const name = station.inputPoint?.pointName || "";
            return name.includes("OTC");
        }) || [];
    }

    private drawCircularCueLayer(stationDTOList?) {
        //Process Visual Cues on Design Profile
        const offSetPoints = this.processOffsetPoints(stationDTOList);
        const otcPoints = this.processOTCPoints(stationDTOList);
        const points = [...offSetPoints, ...this._waterlevelStations, ...otcPoints];

        if (!points?.length) return;
        const pointSource = [] as Graphic[];
        this._circularCueLayer?.removeAll();

        for (let i = 0; i < points.length; i++) {
            const pointName = points[i].inputPoint?.pointName ?? '';

            // Determine percentage based on pointName
            let percentage = '';
            if (/Offset1/i.test(pointName)) {
                percentage = 'quarter 25%';
            } else if (/Offset2/i.test(pointName)) {
                percentage = 'quarter 50%';
            } else if (/Offset3/i.test(pointName)) {
                percentage = 'quarter 75%';
            }
            else if (/WaterLevel/i.test(pointName)) {
                percentage = 'waterLevel';
            }
            else if (/corner/i.test(pointName)) {
                percentage = 'waterLevel';
            }
            else if (/Border/i.test(pointName)) {
                percentage = 'waterLevel';
            }
            else if (/OTC/i.test(pointName)) {
                percentage = 'FreeBoard';
            }
            const point = new Graphic({
                geometry: new Point({
                    spatialReference: this._profileView?.spatialReference,
                    x: points[i].distance * this.getXStretchFactor(),
                    y: points[i].inputPoint.z * AppConstants.profileStretchFactor,
                }),
                symbol: profileSymbolsAndLabels.reachStationSymbol.reachStationSymbol,
                attributes: {
                    ObjectID: i,
                    description: `${percentage}`
                }
            });
            pointSource.push(point);
        }
        this.circularCueLayer?.addMany(pointSource);
        this._profileView?.map.add(this.circularCueLayer)
        // ✅ Attach hover listener only once
        if (!this._hasHoverTooltipListener) {
            this._hasHoverTooltipListener = true;

            this._profileView?.on("pointer-move", (event) => {
                this._profileView?.hitTest(event).then((response) => {
                    const result = response.results.find(r => r.graphic.layer === this._circularCueLayer);

                    if (result) {
                        const graphic = result.graphic;

                        if (this._profileView?.popup) {
                            this._profileView.popup.visibleElements = { closeButton: false };
                            this._profileView.popup.dockEnabled = false;
                            this._profileView.popup.dockOptions = {
                                breakpoint: false
                            };
                        }
                        this._profileView?.popup.open({
                            location: graphic.geometry as Point,
                            content: graphic.attributes.description
                        });
                    } else {
                        this._profileView?.popup.close();
                    }
                });
            });
        }
    }   
    
    private getTCSMaxMinYValues(designStations, originalStations){
        let ymin: number = designStations[0].inputPoint.z;
        let ymax: number = designStations[0].inputPoint.z;

        designStations.forEach(station => {
            const y = station.inputPoint.z;
            if (y < ymin) {
              ymin = y;
            } else if (y > ymax) {
              ymax = y;
            }
        });

        originalStations.forEach(station => {
            const y = station.inputPoint.z;
            if (y < ymin) {
              ymin = y;
            } else if (y > ymax) {
              ymax = y;
            }
        });
        return {ymax, ymin}
    }

    resetProfile() {
        this.cleanMapUp();
        this.cleanGridLayers();
        this._circularCueLayer?.removeAll();
    }

    async getCrossSectionDesignVertices() {
        const originalProfile = this.profiles[ProfileKeyCode[2]] as DynamicProfileClass;
        const designVertices = originalProfile.groundProfilePoints;
        return [designVertices[0], designVertices[designVertices.length - 1]];
    }

    async drawSingleCrossSectionProfiles(profileData){
        this.profileKeys.forEach((keyCode) => {
          this.reset(keyCode);
        });
        const designProfile = this.profiles[ProfileKeyCode[3]] as DynamicProfileClass;
        const originalProfile = this.profiles[ProfileKeyCode[2]] as DynamicProfileClass;
        const designProfileStations = profileData.data?.designProfile?.stationDTOList;
        const originalProfileStations = profileData.data?.originalProfile?.stationDTOList;

        const yValues = this.processProfileElevations([originalProfileStations, designProfileStations]);

        if (originalProfileStations?.length) {
            originalProfile.mapGroundProfilePoints(originalProfileStations);
            originalProfile.ymax = yValues.ymax ? yValues.ymax : undefined;
            originalProfile.ymin = yValues.ymin ? yValues.ymin : undefined;
            const totalDist = originalProfileStations[originalProfileStations.length - 1].distance;
            originalProfile.drawProfileGraphics(totalDist);
        }
    
        if (designProfileStations?.length) {
            designProfile.mapGroundProfilePoints(designProfileStations);
            designProfile.ymax = yValues.ymax ? yValues.ymax : undefined;
            designProfile.ymin = yValues.ymin ? yValues.ymin : undefined;
            const totalDist = designProfileStations[designProfileStations.length - 1].distance;
            designProfile.drawProfileGraphics(totalDist);
        }
    }

    protected processProfileElevations(data) {
        let ymin: number = data[0][0].inputPoint.z;
        let ymax: number = data[0][0].inputPoint.z;

        data.forEach((profile) => {
            profile?.forEach((station) => {
                const y = station.inputPoint.z;
                if (y < ymin) {
                    ymin = y;
                } else if (y > ymax) {
                    ymax = y;
                }
            });
        });

        return {ymax, ymin}
      }
      public get circularCueLayer() {
        const id = 'circularCue_layer';
        const title = 'circular Cue';
        if (!this._circularCueLayer) {
          const layer = this._profileView?.map.findLayerById(id);
          if (layer) {
            this._circularCueLayer = layer as GraphicsLayer;
          } else {
            this._circularCueLayer = new GraphicsLayer({ id, title });
            this._profileView?.map.add(this._circularCueLayer);
          }
        }
        return this._circularCueLayer;
      }
}

export { DynamicCrossSectionProfileStore }